import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* ============================================================================
 *  KTK-303 — Obsidian-flavored vault viewer for the workspace knowledge view.
 *
 *  Layout: left rail = file tree (directories collapsible, current file
 *  highlighted), center = markdown reader OR force-directed graph view
 *  depending on mode. Reader extracts `[[wikilinks]]`, relative `.md`
 *  links and frontmatter slug strings; graph view layers d3-force-style
 *  charge / link / center forces and animates positions on slider changes.
 *
 *  Data is fetched from Hub via `listVaultFiles` + per-path `getVaultFile`;
 *  vaults are typically tens to low-hundreds of files so the parallel fetch
 *  on mount is fine. If we ever cross into thousands a bulk endpoint would
 *  replace the per-path fan-out.
 * ========================================================================== */
import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import "./VaultViewer.css";
const VAULT_LAYERS = [
    { id: "identity", label: "identity", tint: "#c98e6a" },
    { id: "semantic", label: "semantic", tint: "#7a9d6e" },
    { id: "procedural", label: "procedural", tint: "#8a86c2" },
    { id: "episodic", label: "episodic", tint: "#c2a13a" },
    { id: "working", label: "working", tint: "#6a98c9" },
    { id: "_root", label: "/", tint: "#9c9388" },
];
const LAYER_TINT = Object.fromEntries(VAULT_LAYERS.map((l) => [l.id, l.tint]));
function vvLayerOf(path) {
    const i = path.indexOf("/");
    return i < 0 ? "_root" : path.slice(0, i);
}
// Strip YAML frontmatter; return { fm, body }.
function vvStripFrontmatter(src) {
    const m = src.match(/^---\n([\s\S]*?)\n---\n?/);
    if (!m)
        return { fm: null, body: src };
    return { fm: m[1] ?? "", body: src.slice(m[0].length) };
}
function vvBuildIndex(data) {
    const paths = Object.keys(data).sort();
    const byPath = new Map(paths.map((p) => [p, p]));
    const byBare = new Map();
    for (const p of paths) {
        const bare = p.replace(/\.md$/, "");
        byBare.set(bare, p); // identity/the-editor
        const baseOnly = bare.includes("/") ? bare.split("/").pop() : bare;
        if (!byBare.has(baseOnly))
            byBare.set(baseOnly, p); // the-editor
    }
    return { paths, byPath, byBare };
}
function vvResolve(target, fromPath, index) {
    if (!target)
        return null;
    let t = target.trim().replace(/#.*$/, "");
    if (t.startsWith("./") || t.startsWith("../")) {
        const fromDir = fromPath.includes("/") ? fromPath.slice(0, fromPath.lastIndexOf("/")) : "";
        const parts = (fromDir + "/" + t).split("/");
        const stack = [];
        for (const p of parts) {
            if (p === "" || p === ".")
                continue;
            if (p === "..")
                stack.pop();
            else
                stack.push(p);
        }
        const norm = stack.join("/");
        if (index.byPath.has(norm))
            return norm;
    }
    const noExt = t.replace(/\.md$/, "");
    if (index.byBare.has(noExt))
        return index.byBare.get(noExt);
    if (index.byPath.has(t))
        return t;
    if (index.byPath.has(t + ".md"))
        return t + ".md";
    return null;
}
function vvExtractLinks(path, src, index) {
    const { fm, body } = vvStripFrontmatter(src);
    const out = new Set();
    const add = (raw) => {
        const r = vvResolve(raw, path, index);
        if (r && r !== path)
            out.add(r);
    };
    for (const m of body.matchAll(/\[\[([^\]|#]+)(?:\|[^\]]+)?\]\]/g))
        add(m[1] ?? "");
    for (const m of body.matchAll(/\]\(([^)\s]+\.md(?:#[^)]*)?)\)/g))
        add(m[1] ?? "");
    if (fm) {
        for (const m of fm.matchAll(/"([a-z0-9_/-]+)"/gi))
            add(m[1] ?? "");
        for (const m of fm.matchAll(/'([a-z0-9_/-]+)'/gi))
            add(m[1] ?? "");
    }
    return [...out];
}
function vvBuildGraph(data, index) {
    const outbound = new Map();
    const inbound = new Map();
    for (const p of index.paths) {
        outbound.set(p, []);
        inbound.set(p, []);
    }
    const edges = [];
    for (const p of index.paths) {
        const links = vvExtractLinks(p, data[p] ?? "", index);
        for (const t of links) {
            outbound.get(p).push(t);
            inbound.get(t).push(p);
            edges.push({ from: p, to: t });
        }
    }
    const nodes = index.paths.map((p) => ({ id: p, layer: vvLayerOf(p) }));
    return { nodes, edges, outbound, inbound };
}
/* ----- File tree ----- */
function vvIsIndex(p) {
    return /(^|\/)index\.md$/.test(p);
}
function vvBuildTree(paths) {
    const groups = {};
    let rootIndex = null;
    const otherRoot = [];
    for (const p of paths) {
        if (p === "index.md") {
            rootIndex = p;
            continue;
        }
        if (!p.includes("/")) {
            otherRoot.push(p);
            continue;
        }
        const layer = vvLayerOf(p);
        (groups[layer] ||= []).push(p);
    }
    for (const k of Object.keys(groups)) {
        groups[k].sort((a, b) => {
            const ai = vvIsIndex(a) ? 0 : 1;
            const bi = vvIsIndex(b) ? 0 : 1;
            if (ai !== bi)
                return ai - bi;
            return a.localeCompare(b);
        });
    }
    if (otherRoot.length)
        groups._root = otherRoot.sort();
    const order = VAULT_LAYERS.map((l) => l.id).filter((id) => id !== "_root");
    const seen = new Set(order);
    const extras = Object.keys(groups).filter((k) => !seen.has(k) && k !== "_root");
    const ordered = ["_root", ...order, ...extras]
        .filter((k) => groups[k])
        .map((k) => ({ layer: k, files: groups[k] }));
    return { rootIndex, groups: ordered };
}
function VVFileTree({ tree, currentPath, onPick, query, setQuery, expanded, toggleExpanded, }) {
    const { rootIndex, groups } = tree;
    const filtered = useMemo(() => {
        if (!query)
            return groups;
        const q = query.toLowerCase();
        return groups
            .map((g) => ({ ...g, files: g.files.filter((f) => f.toLowerCase().includes(q)) }))
            .filter((g) => g.files.length);
    }, [groups, query]);
    const showRootIndex = rootIndex && (!query || rootIndex.toLowerCase().includes(query.toLowerCase()));
    return (_jsxs("div", { className: "vv-tree", children: [_jsx("div", { className: "vv-tree__search", children: _jsx("input", { className: "vv-tree__searchi", placeholder: "Search files\u2026", value: query, onChange: (e) => setQuery(e.target.value) }) }), _jsxs("div", { className: "vv-tree__scroll", children: [showRootIndex && rootIndex && (_jsxs("button", { type: "button", className: "vv-tree__rootidx" + (rootIndex === currentPath ? " is-active" : ""), onClick: () => onPick(rootIndex), title: rootIndex, children: [_jsx("span", { className: "vv-tree__dot", style: { background: LAYER_TINT._root ?? "#9c9388" } }), _jsx("span", { className: "vv-tree__rootidxl", children: "index" })] })), filtered.map((g) => {
                        const isOpen = !!expanded[g.layer] || !!query;
                        const tint = LAYER_TINT[g.layer] ?? "#9c9388";
                        return (_jsxs("div", { className: "vv-tree__group", children: [_jsxs("button", { type: "button", className: "vv-tree__head" + (isOpen ? " is-open" : ""), onClick: () => toggleExpanded(g.layer), children: [_jsx("span", { className: "vv-tree__chev", children: isOpen ? "▾" : "▸" }), _jsx("span", { className: "vv-tree__dot", style: { background: tint } }), _jsx("span", { className: "vv-tree__layer", children: g.layer === "_root" ? "/" : g.layer }), _jsx("span", { className: "vv-tree__count", children: g.files.length })] }), isOpen && (_jsx("ul", { className: "vv-tree__list", children: g.files.map((f) => {
                                        const stripped = f.includes("/") ? f.slice(f.indexOf("/") + 1) : f;
                                        const display = stripped.replace(/\.md$/, "");
                                        const isCur = f === currentPath;
                                        const isIdx = vvIsIndex(f);
                                        return (_jsx("li", { children: _jsx("button", { type: "button", className: "vv-tree__file" +
                                                    (isCur ? " is-active" : "") +
                                                    (isIdx ? " is-idx" : ""), onClick: () => onPick(f), title: f, children: display }) }, f));
                                    }) }))] }, g.layer));
                    })] })] }));
}
/* ----- Markdown reader ----- */
// KTK-310 — GFM table helpers. A separator row is one or more cells of
// only `-`, optionally bracketed by `:` for alignment; outer pipes are
// allowed but not required. Splitting a row strips the leading +
// trailing pipe and trims each cell.
function vvIsTableSeparator(line) {
    const trimmed = line.trim();
    if (!trimmed.includes("|") && !trimmed.includes("-"))
        return false;
    // strip outer pipes for the cell-by-cell check
    const inner = trimmed.replace(/^\|/, "").replace(/\|$/, "");
    if (!inner.includes("-"))
        return false;
    return inner.split("|").every((cell) => /^\s*:?-{1,}:?\s*$/.test(cell));
}
function vvSplitRow(line) {
    const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
    return trimmed.split("|").map((c) => c.trim());
}
function vvParseAlignments(sep, count) {
    const cells = vvSplitRow(sep);
    const out = [];
    for (let i = 0; i < count; i++) {
        const c = (cells[i] ?? "").trim();
        const left = c.startsWith(":");
        const right = c.endsWith(":");
        if (left && right)
            out.push("center");
        else if (right)
            out.push("right");
        else if (left)
            out.push("left");
        else
            out.push(null);
    }
    return out;
}
function vvEscape(s) {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function vvRenderInline(text, fromPath, index) {
    let s = vvEscape(text);
    s = s.replace(/\[\[([^\]|#]+)(?:\|([^\]]+))?\]\]/g, (_, slug, alias) => {
        const target = vvResolve(slug, fromPath, index);
        const label = vvEscape((alias || slug).trim());
        if (target)
            return `<a class="vv-link" data-vv-link="${vvEscape(target)}">${label}</a>`;
        return `<span class="vv-link vv-link--miss">${label}</span>`;
    });
    s = s.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (_, label, href) => {
        // Internal vault links — relative .md paths or `/v/<layer>/<slug>` URLs
        // emitted by the journalist (KTK-303 backend rewrites
        // [Title](../layer/slug.md) → /v/layer/slug). Both resolve into the
        // viewer; everything else is treated as an external link.
        if (href.startsWith("/v/")) {
            const slug = href.slice(3); // "layer/slug"
            const target = vvResolve(slug, fromPath, index);
            if (target)
                return `<a class="vv-link" data-vv-link="${vvEscape(target)}">${label}</a>`;
            return `<span class="vv-link vv-link--miss">${label}</span>`;
        }
        const target = vvResolve(href, fromPath, index);
        if (target)
            return `<a class="vv-link" data-vv-link="${vvEscape(target)}">${label}</a>`;
        return `<a class="vv-link vv-link--ext" href="${href}" target="_blank" rel="noreferrer">${label}</a>`;
    });
    s = s.replace(/`([^`]+)`/g, (_, c) => `<code class="vv-code">${c}</code>`);
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
    return s;
}
function vvRenderMarkdown(src, fromPath, index) {
    const { fm, body } = vvStripFrontmatter(src);
    const lines = body.split("\n");
    const out = [];
    let i = 0;
    if (fm) {
        const rows = [];
        for (const line of fm.split("\n")) {
            const m = line.match(/^([a-zA-Z0-9_-]+):\s*(.*)$/);
            if (m)
                rows.push([m[1] ?? "", m[2] ?? ""]);
        }
        if (rows.length) {
            out.push('<div class="vv-fm"><div class="vv-fm__h">frontmatter</div><dl>');
            for (const [k, v] of rows) {
                out.push(`<dt>${vvEscape(k)}</dt><dd>${vvRenderInline(v, fromPath, index)}</dd>`);
            }
            out.push("</dl></div>");
        }
    }
    while (i < lines.length) {
        const line = lines[i] ?? "";
        if (/^```/.test(line)) {
            const lang = line.slice(3).trim();
            i++;
            const buf = [];
            while (i < lines.length && !/^```/.test(lines[i] ?? "")) {
                buf.push(lines[i] ?? "");
                i++;
            }
            i++;
            out.push(`<pre class="vv-pre"><code class="vv-codeblock${lang ? " lang-" + vvEscape(lang) : ""}">${vvEscape(buf.join("\n"))}</code></pre>`);
            continue;
        }
        // KTK-310 — GFM table. A header row followed by a separator row of
        // `---` (with optional `:` alignment hints) starts the table; body
        // rows continue until a blank line or non-pipe line. Outer pipes are
        // tolerated and stripped.
        if (line.includes("|") && i + 1 < lines.length && vvIsTableSeparator(lines[i + 1] ?? "")) {
            const headerCells = vvSplitRow(line);
            const aligns = vvParseAlignments(lines[i + 1] ?? "", headerCells.length);
            i += 2;
            const bodyRows = [];
            while (i < lines.length &&
                (lines[i] ?? "").includes("|") &&
                (lines[i] ?? "").trim() !== "") {
                bodyRows.push(vvSplitRow(lines[i] ?? ""));
                i++;
            }
            const renderCell = (raw, align, tag) => {
                const styleAttr = align ? ` style="text-align:${align}"` : "";
                return `<${tag}${styleAttr}>${vvRenderInline(raw, fromPath, index)}</${tag}>`;
            };
            const headHtml = headerCells
                .map((c, ci) => renderCell(c, aligns[ci] ?? null, "th"))
                .join("");
            const bodyHtml = bodyRows
                .map((row) => "<tr>" +
                headerCells
                    .map((_, ci) => renderCell(row[ci] ?? "", aligns[ci] ?? null, "td"))
                    .join("") +
                "</tr>")
                .join("");
            out.push(`<table class="vv-table"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`);
            continue;
        }
        const h = line.match(/^(#{1,6})\s+(.*)$/);
        if (h) {
            const level = (h[1] ?? "").length;
            out.push(`<h${level} class="vv-h vv-h${level}">${vvRenderInline(h[2] ?? "", fromPath, index)}</h${level}>`);
            i++;
            continue;
        }
        if (/^>\s?/.test(line)) {
            const buf = [];
            while (i < lines.length && /^>\s?/.test(lines[i] ?? "")) {
                buf.push((lines[i] ?? "").replace(/^>\s?/, ""));
                i++;
            }
            out.push(`<blockquote class="vv-bq">${vvRenderInline(buf.join(" "), fromPath, index)}</blockquote>`);
            continue;
        }
        if (/^[-*]\s+/.test(line)) {
            const buf = [];
            while (i < lines.length && /^[-*]\s+/.test(lines[i] ?? "")) {
                buf.push((lines[i] ?? "").replace(/^[-*]\s+/, ""));
                i++;
            }
            out.push('<ul class="vv-ul">' +
                buf.map((b) => `<li>${vvRenderInline(b, fromPath, index)}</li>`).join("") +
                "</ul>");
            continue;
        }
        if (/^\d+\.\s+/.test(line)) {
            const buf = [];
            while (i < lines.length && /^\d+\.\s+/.test(lines[i] ?? "")) {
                buf.push((lines[i] ?? "").replace(/^\d+\.\s+/, ""));
                i++;
            }
            out.push('<ol class="vv-ol">' +
                buf.map((b) => `<li>${vvRenderInline(b, fromPath, index)}</li>`).join("") +
                "</ol>");
            continue;
        }
        if (line.trim() === "") {
            i++;
            continue;
        }
        const buf = [line];
        i++;
        while (i < lines.length &&
            (lines[i] ?? "").trim() !== "" &&
            !/^(#{1,6}\s|>\s?|[-*]\s+|\d+\.\s+|```)/.test(lines[i] ?? "")) {
            buf.push(lines[i] ?? "");
            i++;
        }
        out.push(`<p class="vv-p">${vvRenderInline(buf.join(" "), fromPath, index)}</p>`);
    }
    return out.join("\n");
}
function VVReader({ path, src, index, onPick, backlinks, outbound, }) {
    const ref = useRef(null);
    const html = useMemo(() => vvRenderMarkdown(src, path, index), [src, path, index]);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const handler = (e) => {
            const t = e.target;
            if (!(t instanceof Element))
                return;
            const a = t.closest("[data-vv-link]");
            if (!a)
                return;
            e.preventDefault();
            const slug = a.getAttribute("data-vv-link");
            if (slug)
                onPick(slug);
        };
        el.addEventListener("click", handler);
        return () => el.removeEventListener("click", handler);
    }, [onPick]);
    const fname = path.includes("/") ? path.slice(path.lastIndexOf("/") + 1) : path;
    const dir = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "/";
    return (_jsxs("div", { className: "vv-reader", children: [_jsxs("div", { className: "vv-reader__crumbs", children: [_jsx("span", { className: "vv-reader__layer", style: { color: LAYER_TINT[vvLayerOf(path)] ?? "var(--ink-3)" }, children: dir }), _jsx("span", { className: "vv-reader__sep", children: "/" }), _jsx("span", { className: "vv-reader__name", children: fname })] }), _jsx("article", { className: "vv-md", ref: ref, dangerouslySetInnerHTML: { __html: html } }), _jsxs("div", { className: "vv-rels", children: [_jsxs("div", { children: [_jsxs("div", { className: "vv-rels__h", children: ["Outbound \u00B7 ", outbound.length] }), outbound.length === 0 && _jsx("div", { className: "vv-rels__empty", children: "No outbound links." }), _jsx("ul", { className: "vv-rels__list", children: outbound.map((p) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "vv-rels__link", onClick: () => onPick(p), children: [_jsx("span", { className: "vv-rels__dot", style: { background: LAYER_TINT[vvLayerOf(p)] ?? "#9c9388" } }), p.replace(/\.md$/, "")] }) }, p))) })] }), _jsxs("div", { children: [_jsxs("div", { className: "vv-rels__h", children: ["Backlinks \u00B7 ", backlinks.length] }), backlinks.length === 0 && _jsx("div", { className: "vv-rels__empty", children: "Nothing links here yet." }), _jsx("ul", { className: "vv-rels__list", children: backlinks.map((p) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "vv-rels__link", onClick: () => onPick(p), children: [_jsx("span", { className: "vv-rels__dot", style: { background: LAYER_TINT[vvLayerOf(p)] ?? "#9c9388" } }), p.replace(/\.md$/, "")] }) }, p))) })] })] })] }));
}
const VV_FORCE_DEFAULTS = {
    centerForce: 0.5,
    repelForce: 10,
    linkForce: 1.0,
    linkDistance: 250,
};
const VV_DISPLAY_DEFAULTS = {
    textFadeThreshold: 3.03,
    nodeSize: 1.64,
    linkThickness: 3,
};
// Continuous d3-force-style sim. Mutates positions in place, bumps a tick
// counter so React re-renders. Slider changes re-heat the sim instead of
// rebuilding from scratch so motion looks fluid like Obsidian's graph view.
function useVVForceSim(nodes, edges, w, h, forces) {
    const posRef = useRef(new Map());
    const adjRef = useRef([]);
    const forcesRef = useRef(forces);
    forcesRef.current = forces;
    const alphaRef = useRef(1);
    const [, setTick] = useState(0);
    // Seed positions when the node set changes. Scatter inside a 320×220 box
    // (NOT a single ring — packing N nodes onto one circle puts neighbours
    // ~px apart and the inverse-square charge force catapults them on tick 1).
    useEffect(() => {
        const next = new Map();
        const cx = w / 2;
        const cy = h / 2;
        const BW = 320;
        const BH = 220;
        for (let i = 0; i < nodes.length; i++) {
            const id = nodes[i].id;
            const prev = posRef.current.get(id);
            if (prev) {
                next.set(id, prev);
            }
            else {
                const r1 = ((i * 9301 + 49297) % 233280) / 233280;
                const r2 = ((i * 49297 + 9301) % 233280) / 233280;
                next.set(id, {
                    x: cx + (r1 - 0.5) * BW,
                    y: cy + (r2 - 0.5) * BH,
                    vx: 0,
                    vy: 0,
                });
            }
        }
        posRef.current = next;
        alphaRef.current = 1;
    }, [nodes, w, h]);
    // Build adjacency + per-link weights (d3-force-link style). strength =
    // 1/min(deg) so hub nodes (e.g. an "AI" tag with ~30 connections) don't
    // get pulled N× harder than a leaf; bias splits correction inversely
    // by degree so the higher-degree endpoint moves less.
    useEffect(() => {
        const have = (id) => posRef.current.has(id);
        const valid = edges.filter((e) => have(e.from) && have(e.to));
        const deg = new Map();
        for (const e of valid) {
            deg.set(e.from, (deg.get(e.from) ?? 0) + 1);
            deg.set(e.to, (deg.get(e.to) ?? 0) + 1);
        }
        adjRef.current = valid.map((e) => {
            const da = deg.get(e.from) ?? 1;
            const db = deg.get(e.to) ?? 1;
            return {
                from: e.from,
                to: e.to,
                strength: 1 / Math.min(da, db),
                bias: da / (da + db),
            };
        });
    }, [edges, nodes]);
    // Re-heat on slider changes so the sim re-expresses the new params.
    useEffect(() => {
        alphaRef.current = Math.max(alphaRef.current, 0.6);
    }, [forces]);
    useEffect(() => {
        let raf = 0;
        const VELOCITY_DECAY = 0.4;
        const ALPHA_DECAY = 0.0228;
        const ALPHA_MIN = 0.001;
        const cx = w / 2;
        const cy = h / 2;
        const step = () => {
            const f = forcesRef.current;
            const pos = posRef.current;
            const adj = adjRef.current;
            const ids = [...pos.keys()];
            const N = ids.length;
            const alpha = alphaRef.current;
            if (alpha > ALPHA_MIN && N > 0) {
                // Charge: pairwise repulsion; clamp d² for close pairs so tick-1
                // catapults can't happen. Scaled ×80 so the 0–20 slider spans
                // "barely separates clusters" → "explodes them apart" with real
                // expressive range; d3-force defaults to −30 but is tuned for
                // sparser graphs than this hub-and-spoke vault.
                const charge = -f.repelForce * 80;
                const MIN_D2 = 100;
                const MAX_D2 = 250000;
                for (let i = 0; i < N; i++) {
                    const a = pos.get(ids[i]);
                    for (let j = i + 1; j < N; j++) {
                        const b = pos.get(ids[j]);
                        let dx = b.x - a.x;
                        let dy = b.y - a.y;
                        let d2 = dx * dx + dy * dy;
                        if (d2 > MAX_D2)
                            continue;
                        if (d2 < 1) {
                            dx = Math.random() - 0.5;
                            dy = Math.random() - 0.5;
                            d2 = dx * dx + dy * dy + 1;
                        }
                        if (d2 < MIN_D2)
                            d2 = MIN_D2;
                        const fr = (charge * alpha) / d2;
                        a.vx += dx * fr;
                        a.vy += dy * fr;
                        b.vx -= dx * fr;
                        b.vy -= dy * fr;
                    }
                }
                // Link: spring toward target distance. Per-link strength = 1/min(deg)
                // so hub nodes don't get pulled apart by every spoke; bias splits
                // the correction between endpoints inversely to their degree. Run
                // the relaxation 3× per tick so links feel stiff without overshoot
                // through the velocity cap (d3-force exposes this same knob).
                const LINK_ITERS = 3;
                const linkScale = f.linkForce;
                const linkDist = f.linkDistance;
                for (let it = 0; it < LINK_ITERS; it++) {
                    for (const e of adj) {
                        const a = pos.get(e.from);
                        const b = pos.get(e.to);
                        if (!a || !b)
                            continue;
                        const dx = b.x - a.x;
                        const dy = b.y - a.y;
                        const d = Math.sqrt(dx * dx + dy * dy) || 1;
                        const k = ((d - linkDist) / d) * alpha * linkScale * e.strength;
                        a.vx += dx * k * (1 - e.bias);
                        a.vy += dy * k * (1 - e.bias);
                        b.vx -= dx * k * e.bias;
                        b.vy -= dy * k * e.bias;
                    }
                }
                // Centre + integrate. Centre force = per-node spring toward
                // (cx, cy). VEL_CAP keeps any single-tick impulse from launching
                // a node off-screen — d3-force has no equivalent because it
                // relies on Barnes-Hut + careful default strengths to keep
                // impulses tame.
                const cf = f.centerForce * alpha * 0.1;
                const VEL_CAP = 50;
                for (let i = 0; i < N; i++) {
                    const p = pos.get(ids[i]);
                    p.vx += (cx - p.x) * cf;
                    p.vy += (cy - p.y) * cf;
                    p.vx *= 1 - VELOCITY_DECAY;
                    p.vy *= 1 - VELOCITY_DECAY;
                    if (p.vx > VEL_CAP)
                        p.vx = VEL_CAP;
                    if (p.vx < -VEL_CAP)
                        p.vx = -VEL_CAP;
                    if (p.vy > VEL_CAP)
                        p.vy = VEL_CAP;
                    if (p.vy < -VEL_CAP)
                        p.vy = -VEL_CAP;
                    p.x += p.vx;
                    p.y += p.vy;
                }
                alphaRef.current = alpha - ALPHA_DECAY * (alpha - ALPHA_MIN);
                setTick((t) => (t + 1) & 0xffff);
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [w, h]);
    return { positions: posRef.current };
}
/* ----- Graph controls popover ----- */
const VV_BOUNDS = {
    textFadeMin: 2,
    textFadeMax: 5,
    nodeSizeMin: 0,
    nodeSizeMax: 5,
    linkThickMin: 1,
    linkThickMax: 6,
    centerForceMin: 0,
    centerForceMax: 1,
    repelForceMin: 0,
    repelForceMax: 20,
    linkForceMin: 0,
    linkForceMax: 1,
    linkDistMin: 30,
    linkDistMax: 500,
};
function stepFor(min, max) {
    const span = Math.max(1e-6, max - min);
    const raw = span / 200;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    return Math.max(mag, raw < mag * 2 ? mag : mag * (raw < mag * 5 ? 2 : 5));
}
function VVSlider({ label, value, min, max, step, onChange, format, }) {
    return (_jsxs("label", { className: "vv-ctrl", children: [_jsxs("span", { className: "vv-ctrl__l", children: [_jsx("span", { children: label }), _jsx("span", { className: "vv-ctrl__v", children: format ? format(value) : value.toFixed(step < 0.01 ? 4 : step < 1 ? 2 : 0) })] }), _jsx("input", { type: "range", min: min, max: max, step: step, value: value, onChange: (e) => onChange(parseFloat(e.target.value)) })] }));
}
function VVGraphControls({ display, setDisplay, forces, setForces, onReset, }) {
    const [open, setOpen] = useState(false);
    const b = VV_BOUNDS;
    const stop = (e) => e.stopPropagation();
    return (_jsxs("div", { className: "vv-graphctrls", onWheel: stop, onMouseDown: stop, children: [_jsx("button", { type: "button", className: "vv-graphctrls__btn" + (open ? " is-open" : ""), onClick: () => setOpen((o) => !o), title: "Graph controls", children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "12", cy: "12", r: "3" }), _jsx("path", { d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" })] }) }), open && (_jsxs("div", { className: "vv-graphctrls__pop", children: [_jsxs("div", { className: "vv-graphctrls__sec", children: [_jsx("div", { className: "vv-graphctrls__h", children: "Display" }), _jsx(VVSlider, { label: "Text fade threshold", min: b.textFadeMin, max: b.textFadeMax, step: stepFor(b.textFadeMin, b.textFadeMax), value: display.textFadeThreshold, onChange: (v) => setDisplay({ ...display, textFadeThreshold: v }), format: (v) => v.toFixed(2) }), _jsx(VVSlider, { label: "Node size", min: b.nodeSizeMin, max: b.nodeSizeMax, step: stepFor(b.nodeSizeMin, b.nodeSizeMax), value: display.nodeSize, onChange: (v) => setDisplay({ ...display, nodeSize: v }), format: (v) => v.toFixed(2) }), _jsx(VVSlider, { label: "Link thickness", min: b.linkThickMin, max: b.linkThickMax, step: stepFor(b.linkThickMin, b.linkThickMax), value: display.linkThickness, onChange: (v) => setDisplay({ ...display, linkThickness: v }), format: (v) => v.toFixed(2) })] }), _jsxs("div", { className: "vv-graphctrls__sec", children: [_jsx("div", { className: "vv-graphctrls__h", children: "Forces" }), _jsx(VVSlider, { label: "Center force", min: b.centerForceMin, max: b.centerForceMax, step: stepFor(b.centerForceMin, b.centerForceMax), value: forces.centerForce, onChange: (v) => setForces({ ...forces, centerForce: v }), format: (v) => v.toFixed(2) }), _jsx(VVSlider, { label: "Repel force", min: b.repelForceMin, max: b.repelForceMax, step: stepFor(b.repelForceMin, b.repelForceMax), value: forces.repelForce, onChange: (v) => setForces({ ...forces, repelForce: v }), format: (v) => v.toFixed(2) }), _jsx(VVSlider, { label: "Link force", min: b.linkForceMin, max: b.linkForceMax, step: stepFor(b.linkForceMin, b.linkForceMax), value: forces.linkForce, onChange: (v) => setForces({ ...forces, linkForce: v }), format: (v) => v.toFixed(2) }), _jsx(VVSlider, { label: "Link distance", min: b.linkDistMin, max: b.linkDistMax, step: stepFor(b.linkDistMin, b.linkDistMax), value: forces.linkDistance, onChange: (v) => setForces({ ...forces, linkDistance: v }), format: (v) => v.toFixed(0) + " px" })] }), _jsx("button", { type: "button", className: "vv-graphctrls__reset", onClick: onReset, children: "Reset to defaults" })] }))] }));
}
/* ----- Graph view ----- */
function VVGraph({ nodes, edges, outbound, inbound, currentPath, onPick, display, setDisplay, forces, setForces, onResetControls, }) {
    const W = 1200;
    const H = 760;
    const svgRef = useRef(null);
    const { positions: layout } = useVVForceSim(nodes, edges, W, H, forces);
    const [hover, setHover] = useState(null);
    const [view, setView] = useState({ x: 0, y: 0, k: 1 });
    const dragRef = useRef(null);
    const focusId = hover ?? currentPath;
    const highlight = useMemo(() => {
        if (!focusId)
            return null;
        const set = new Set([focusId]);
        for (const t of outbound.get(focusId) ?? [])
            set.add(t);
        for (const t of inbound.get(focusId) ?? [])
            set.add(t);
        return set;
    }, [focusId, outbound, inbound]);
    const isDim = (id) => !!highlight && !highlight.has(id);
    const onMouseDown = (e) => {
        dragRef.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
    };
    useEffect(() => {
        const move = (e) => {
            const d = dragRef.current;
            if (!d)
                return;
            setView((v) => ({ ...v, x: d.vx + (e.clientX - d.x), y: d.vy + (e.clientY - d.y) }));
        };
        const up = () => {
            dragRef.current = null;
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
        };
    }, []);
    const onWheel = (e) => {
        e.preventDefault();
        const target = svgRef.current ?? e.currentTarget;
        const rect = target.getBoundingClientRect();
        const mx = (e.clientX - rect.left) * (W / rect.width);
        const my = (e.clientY - rect.top) * (H / rect.height);
        const dz = e.deltaY > 0 ? 0.9 : 1.1;
        setView((v) => {
            const nk = Math.max(0.3, Math.min(5, v.k * dz));
            const ratio = nk / v.k;
            return {
                k: nk,
                x: mx - (mx - v.x) * ratio,
                y: my - (my - v.y) * ratio,
            };
        });
    };
    const labelOpacity = Math.max(0, Math.min(1, (view.k - display.textFadeThreshold + 0.25) / 0.25));
    return (_jsxs("div", { className: "vv-graph", children: [_jsxs("div", { className: "vv-graph__legend", children: [VAULT_LAYERS.filter((l) => l.id !== "_root").map((l) => (_jsxs("span", { className: "vv-graph__legitem", children: [_jsx("span", { className: "vv-graph__swatch", style: { background: l.tint } }), l.label] }, l.id))), _jsx("span", { className: "vv-graph__hint", children: "drag to pan \u00B7 scroll to zoom \u00B7 click a node to open" })] }), _jsx("svg", { ref: svgRef, className: "vv-graph__svg", viewBox: `0 0 ${W} ${H}`, preserveAspectRatio: "xMidYMid meet", onMouseDown: onMouseDown, onWheel: onWheel, children: _jsxs("g", { transform: `translate(${view.x} ${view.y}) scale(${view.k})`, children: [edges.map((e, i) => {
                            const a = layout.get(e.from);
                            const b = layout.get(e.to);
                            if (!a || !b)
                                return null;
                            const dim = highlight && !(highlight.has(e.from) && highlight.has(e.to));
                            return (_jsx("line", { x1: a.x, y1: a.y, x2: b.x, y2: b.y, strokeWidth: display.linkThickness, className: "vv-graph__edge" + (dim ? " is-dim" : "") }, i));
                        }), nodes.map((n) => {
                            const p = layout.get(n.id);
                            if (!p)
                                return null;
                            // Hierarchy-driven sizing: each folder-depth level halves the
                            // diameter, with index.md collapsed onto its parent depth so
                            // root + per-layer index nodes dominate visually.
                            let lvl;
                            if (n.id === "index.md") {
                                lvl = 0;
                            }
                            else {
                                const parts = n.id.split("/");
                                const isIdx = parts[parts.length - 1] === "index.md";
                                lvl = isIdx ? parts.length - 1 : parts.length;
                            }
                            const baseR = Math.max(2, 14 * Math.pow(0.5, lvl));
                            const r = baseR * display.nodeSize;
                            const dim = isDim(n.id);
                            const isFocused = n.id === focusId;
                            const op = isFocused ? 1 : labelOpacity;
                            const fname = n.id.includes("/") ? n.id.slice(n.id.lastIndexOf("/") + 1) : n.id;
                            const stem = fname.replace(/\.md$/, "");
                            let label = stem;
                            if (stem === "index" && n.id.includes("/")) {
                                const parent = n.id.slice(0, n.id.lastIndexOf("/"));
                                const folder = parent.includes("/") ? parent.slice(parent.lastIndexOf("/") + 1) : parent;
                                label = `${folder}/index`;
                            }
                            return (_jsxs("g", { style: { transform: `translate(${p.x}px, ${p.y}px)` }, className: "vv-graph__node" +
                                    (dim ? " is-dim" : "") +
                                    (n.id === currentPath ? " is-current" : ""), onMouseEnter: () => setHover(n.id), onMouseLeave: () => setHover(null), onClick: () => onPick(n.id), children: [_jsx("circle", { r: r, fill: LAYER_TINT[n.layer] ?? "#9c9388" }), op > 0.01 && (_jsx("text", { y: -r - 6, textAnchor: "middle", className: "vv-graph__label", opacity: op, children: label }))] }, n.id));
                        })] }) }), _jsx(VVGraphControls, { display: display, setDisplay: setDisplay, forces: forces, setForces: setForces, onReset: onResetControls }), focusId && (_jsxs("div", { className: "vv-graph__panel", children: [_jsx("div", { className: "vv-graph__panelh", children: focusId }), _jsxs("div", { className: "vv-graph__panelmeta", children: ["outbound ", _jsx("span", { className: "ws-mono", children: (outbound.get(focusId) ?? []).length }), " \u00B7 backlinks ", _jsx("span", { className: "ws-mono", children: (inbound.get(focusId) ?? []).length })] })] }))] }));
}
export function VaultViewer({ workspaceId, initialPath }) {
    const ctx = useDataContext();
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [path, setPath] = useState(initialPath ?? null);
    const [query, setQuery] = useState("");
    const [mode, setMode] = useState("read");
    const [display, setDisplay] = useState(VV_DISPLAY_DEFAULTS);
    const [forces, setForces] = useState(VV_FORCE_DEFAULTS);
    const settingsLoadedRef = useRef(false);
    // KTK-306 — every layer starts collapsed; user-driven expansion only.
    const [expanded, setExpanded] = useState(() => Object.fromEntries(VAULT_LAYERS.map((l) => [l.id, false])));
    // Hub-persisted graph display + forces settings (per-user, workspace-
    // scoped). `vault_graph` lives under the generic `member-settings/:key`
    // namespace Rocky introduced for KTK-303. Read once on mount; PUT (or
    // null for reset) on slider changes, debounced 400 ms.
    useEffect(() => {
        let cancelled = false;
        settingsLoadedRef.current = false;
        (async () => {
            try {
                const stored = (await ctx.hub.getMemberSetting(workspaceId, "vault_graph"));
                if (cancelled)
                    return;
                if (stored?.display) {
                    setDisplay({ ...VV_DISPLAY_DEFAULTS, ...stored.display });
                }
                if (stored?.forces) {
                    setForces({ ...VV_FORCE_DEFAULTS, ...stored.forces });
                }
            }
            catch {
                // Network blip / 404 (member-settings not seeded yet) — silently
                // fall back to defaults; the user's first change will persist.
            }
            finally {
                if (!cancelled)
                    settingsLoadedRef.current = true;
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ctx, workspaceId]);
    // Debounced PUT when sliders change. Skip the initial paint (until the
    // GET has resolved) so we don't clobber stored prefs with defaults.
    useEffect(() => {
        if (!settingsLoadedRef.current)
            return;
        const timer = setTimeout(() => {
            const payload = { display, forces };
            void ctx.hub.setMemberSetting(workspaceId, "vault_graph", payload).catch(() => {
                // Best-effort; sliders still work locally for the session.
            });
        }, 400);
        return () => clearTimeout(timer);
    }, [ctx, workspaceId, display, forces]);
    const resetGraphControls = useCallback(() => {
        setDisplay(VV_DISPLAY_DEFAULTS);
        setForces(VV_FORCE_DEFAULTS);
        if (settingsLoadedRef.current) {
            void ctx.hub.setMemberSetting(workspaceId, "vault_graph", null).catch(() => { });
        }
    }, [ctx, workspaceId]);
    // Fetch the vault on mount: list files → parallel fetch each body. The
    // graph + reader both need the full text to extract links and resolve
    // wikilinks, so a single all-or-nothing load is simplest. With 30-150
    // files this lands in well under a second; if vaults grow large we'll
    // swap in a bulk endpoint.
    useEffect(() => {
        let cancelled = false;
        setLoadError(null);
        setData(null);
        (async () => {
            try {
                const files = await ctx.hub.listVaultFiles(workspaceId);
                const entries = await Promise.all(files.map(async (f) => {
                    const file = await ctx.hub.getVaultFile(workspaceId, f.path);
                    return [f.path, file.content];
                }));
                if (cancelled)
                    return;
                setData(Object.fromEntries(entries));
            }
            catch (err) {
                if (cancelled)
                    return;
                setLoadError(err instanceof Error ? err.message : String(err));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ctx, workspaceId]);
    const index = useMemo(() => (data ? vvBuildIndex(data) : null), [data]);
    const graph = useMemo(() => (data && index ? vvBuildGraph(data, index) : null), [data, index]);
    const tree = useMemo(() => (index ? vvBuildTree(index.paths) : null), [index]);
    // Default-path selection once the index is available.
    useEffect(() => {
        if (!index || path)
            return;
        const fallback = (index.byPath.has("identity/the-editor.md") && "identity/the-editor.md") ||
            index.paths[0];
        if (fallback)
            setPath(fallback);
    }, [index, path]);
    // If the parent updates `initialPath` (deep-link nav), follow it.
    useEffect(() => {
        if (initialPath && index && index.byPath.has(initialPath)) {
            setPath(initialPath);
            const layer = vvLayerOf(initialPath);
            setExpanded((e) => (e[layer] ? e : { ...e, [layer]: true }));
        }
    }, [initialPath, index]);
    const onPick = useCallback((p) => {
        setPath(p);
        setMode("read");
        const layer = vvLayerOf(p);
        setExpanded((e) => (e[layer] ? e : { ...e, [layer]: true }));
    }, []);
    const toggleExpanded = useCallback((layer) => setExpanded((e) => ({ ...e, [layer]: !e[layer] })), []);
    if (loadError) {
        return (_jsxs("div", { className: "vv-empty", children: [_jsx("h2", { className: "vv-empty__h", children: "Couldn't load the vault" }), _jsx("div", { children: loadError })] }));
    }
    if (!data || !index || !graph || !tree) {
        return (_jsx("div", { className: "vv-empty", children: _jsx("div", { children: "Loading vault\u2026" }) }));
    }
    if (index.paths.length === 0) {
        return (_jsxs("div", { className: "vv-empty", children: [_jsx("h2", { className: "vv-empty__h", children: "This vault is empty" }), _jsx("div", { children: "Once your journalists run a digest pass, the notes will appear here." })] }));
    }
    const totalLinks = graph.edges.length;
    const activePath = path && index.byPath.has(path) ? path : index.paths[0];
    const src = data[activePath] ?? "";
    return (_jsxs("div", { className: "vv-shell", children: [_jsx(VVFileTree, { tree: tree, currentPath: activePath, onPick: onPick, query: query, setQuery: setQuery, expanded: expanded, toggleExpanded: toggleExpanded }), _jsxs("div", { className: "vv-main", children: [_jsxs("div", { className: "vv-toolbar", children: [_jsxs("div", { className: "vv-toolbar__seg", children: [_jsx("button", { type: "button", className: "vv-segbtn" + (mode === "read" ? " is-active" : ""), onClick: () => setMode("read"), children: "Reader" }), _jsx("button", { type: "button", className: "vv-segbtn" + (mode === "graph" ? " is-active" : ""), onClick: () => setMode("graph"), children: "Graph" })] }), _jsx(ToolbarMeta, { total: index.paths.length, links: totalLinks })] }), mode === "read" ? (_jsx(VVReader, { path: activePath, src: src, index: index, onPick: onPick, backlinks: graph.inbound.get(activePath) ?? [], outbound: graph.outbound.get(activePath) ?? [] })) : (_jsx(VVGraph, { nodes: graph.nodes, edges: graph.edges, outbound: graph.outbound, inbound: graph.inbound, currentPath: activePath, onPick: onPick, display: display, setDisplay: setDisplay, forces: forces, setForces: setForces, onResetControls: resetGraphControls }))] })] }));
}
function ToolbarMeta({ total, links }) {
    const monoStyle = { fontFamily: "var(--mono)", color: "var(--ink)" };
    return (_jsxs("div", { className: "vv-toolbar__meta", children: [_jsx("span", { style: monoStyle, children: total }), " files \u00B7", " ", _jsx("span", { style: monoStyle, children: links }), " links"] }));
}
//# sourceMappingURL=VaultViewer.js.map